import { useCallback, useEffect, useRef, useState } from 'react';
import { aiCandidateAPI } from '../../../lib/aiInterviewApi';
import { bestDescriptor, detectFaces, distance, frameToJpeg, loadFaceKit } from '../faceKit';

const FACE_EVERY_MS = 4000;
const REVERIFY_FIRST_MS = 120000;
const REVERIFY_EVERY_MS = 240000;

// Client-side integrity signals. They only route the interview to human review.
export function useProctor({ token, ctx, active, videoRef, streamRef, clock, descriptor }) {
  const queue = useRef([]);
  const [notice, setNotice] = useState(null);
  const [fsOut, setFsOut] = useState(false);
  const noticeTimer = useRef(null);

  const say = useCallback((msg) => {
    setNotice(msg);
    clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 7000);
  }, []);

  const flush = useCallback(async () => {
    if (!queue.current.length) return;
    const items = queue.current.splice(0, 25);
    try { await aiCandidateAPI.signals(token, items); } catch (e) { queue.current.unshift(...items); }
  }, [token]);

  const push = useCallback((s, now = false) => {
    queue.current.push({ ts_ms: clock(), ...s });
    if (now) flush();
  }, [clock, flush]);

  const snapshot = useCallback(async () => {
    try {
      const { blob } = await frameToJpeg(videoRef.current, 480, 0.7);
      const fd = new FormData();
      fd.append('image', blob, 'snap.jpg');
      return (await aiCandidateAPI.snapshot(token, fd)).data.evidence_ref;
    } catch (e) {
      return null;
    }
  }, [token, videoRef]);

  // Focus, visibility, full-screen.
  useEffect(() => {
    if (!active) return undefined;
    let hiddenAt = null;
    let blurAt = null;
    const onVis = () => {
      if (document.hidden) { hiddenAt = Date.now(); return; }
      if (hiddenAt) {
        const dur = Date.now() - hiddenAt;
        queue.current.push({ type: 'tab_hidden', duration_ms: dur, ts_ms: Math.max(0, clock() - dur), details: `Hidden for ${Math.round(dur / 1000)}s` });
        flush();
        say('Please keep the interview tab open until you finish.');
      }
      hiddenAt = null;
    };
    const onBlur = () => { blurAt = Date.now(); };
    const onFocus = () => {
      if (blurAt && !hiddenAt && Date.now() - blurAt > 2000) push({ type: 'window_blur', duration_ms: Date.now() - blurAt });
      blurAt = null;
    };
    const onFs = () => {
      if (!ctx.fullscreen_required) return;
      const out = !document.fullscreenElement;
      setFsOut(out);
      if (out) push({ type: 'fullscreen_exit' });
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', onFocus);
    document.addEventListener('fullscreenchange', onFs);
    const t = setInterval(flush, 8000);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('fullscreenchange', onFs);
      clearInterval(t);
      flush();
    };
  }, [active, ctx.fullscreen_required, clock, flush, push, say]);

  // Camera track health.
  useEffect(() => {
    const track = streamRef.current?.getVideoTracks()[0];
    if (!active || !track) return undefined;
    const off = () => { push({ type: 'camera_off', details: 'Camera stopped or was blocked' }, true); say('Your camera stopped. Please turn it back on.'); };
    track.addEventListener('ended', off);
    return () => track.removeEventListener('ended', off);
  }, [active, streamRef, push, say]);

  // Face count + periodic re-verification (skipped for light proctoring).
  useEffect(() => {
    if (!active || ctx.proctoring_level === 'light') return undefined;
    let stopped = false;
    let zero = 0;
    let multi = 0;
    let lastReverify = Date.now() - (REVERIFY_EVERY_MS - REVERIFY_FIRST_MS);
    const tick = async () => {
      const v = videoRef.current;
      if (stopped || !v || v.readyState < 2) return;
      try {
        await loadFaceKit();
        const n = (await detectFaces(v, { size: 224 })).length;
        zero = n === 0 ? zero + 1 : 0;
        multi = n > 1 ? multi + 1 : 0;
        if (zero === 3) { push({ type: 'no_face', duration_ms: FACE_EVERY_MS * 3 }); say('We can\'t see your face. Please face the camera.'); }
        if (multi === 2) {
          const ref = await snapshot();
          push({ type: 'multiple_faces', evidence_ref: ref, details: `${n} faces in view` }, true);
          say('Only you should be in view during the interview.');
        }
        if (ctx.reverify && descriptor && Date.now() - lastReverify >= REVERIFY_EVERY_MS) {
          lastReverify = Date.now();
          const d = await bestDescriptor(v, 320);
          const dist = distance(d.descriptor, descriptor);
          const ok = dist !== null && dist <= 0.55;
          const ref = ok ? null : await snapshot();
          await aiCandidateAPI.reverify(token, { distance: dist, faces: d.count, evidence_ref: ref });
        }
      } catch (e) { /* face kit unavailable: skip silently */ }
    };
    const t = setInterval(tick, FACE_EVERY_MS);
    return () => { stopped = true; clearInterval(t); };
  }, [active, ctx.proctoring_level, ctx.reverify, descriptor, token, videoRef, push, say, snapshot]);

  useEffect(() => () => clearTimeout(noticeTimer.current), []);

  const backToFullscreen = () => document.documentElement.requestFullscreen?.().catch(() => {});
  return { notice, fsOut, backToFullscreen, push, flush };
}
